import api from './api';

/**
 * A teljes jegylista betöltése, lapozással (#164).
 *
 * A `GET /tickets` szerveroldali alapértelmezése `limit = 20`
 * (`backend/src/tickets/tickets.service.ts:396`). Három admin képernyő
 * oldalméret NÉLKÜL hívta, és a visszakapott 20 sort tekintette a teljes
 * halmaznak: a beléptető lista, az esemény „Résztvevők" füle és — más okból —
 * a Jegyek lapozója. A Connect+Grow 2026 eseményen 176 jegy van, tehát a
 * szervező 20-at látott belőlük.
 *
 * A szerver alapértelmezése SZÁNDÉKOSAN marad 20: elmozdítása minden meglévő
 * hívó válaszát elmozdítaná, a panelen kívülieket is. A hívók kérnek explicit
 * oldalméretet, és ez a függvény lapozza végig a listát.
 *
 * Két szabály, amit ez a modul tart be a hívói helyett:
 *
 * 1. **A darabszám a `meta.total`-ból jön, nem a betöltött tömb hosszából.** A
 *    „Résztvevők (20)" felirat pontosan abból állt elő, hogy valaki a tömb
 *    hosszát írta ki darabszámnak. Ha a lista csonka marad (felső korlát,
 *    megszakadt lapozás), a lista rövidül — a SZÁM nem hazudik.
 * 2. **Az elavulás-őr minden oldal után lefut.** A beléptető képernyőn a
 *    szervező eseményt válthat, miközben az előző lista még tölt; a meglévő őr
 *    egyetlen kérésre volt írva, lapozásnál oldalanként kell nézni.
 */

/**
 * Egy kérés oldalmérete.
 *
 * 500, nem 20 vagy 50: a reális eseményméret (a Connect+Grow 176 jegye) így
 * EGYETLEN kérésből megjön, és a lapozás csak a kivételes esetben indul el. A
 * backend `short` sebességkorlátja 30 kérés/másodperc kliens-címenként, a
 * lapozás pedig sorosan megy, tehát a nagy oldalméret itt kevesebb kérés, nem
 * nagyobb kockázat. A Jegyek oldal statisztika-lekérése `limit: 10000`-rel
 * megy ugyanezen a végponton, tehát az 500 jóval a bevált méret alatt van.
 */
export const TICKET_PAGE_SIZE = 500;

/**
 * Hány oldalt kérünk le legfeljebb — 500-as oldalmérettel 10 000 jegy.
 *
 * Nem teljesítmény-korlát, hanem védőkorlát: ha a szerver `meta.total`-ja
 * sosem teljesül (hibás számlálás, közben növekvő lista), a ciklus e nélkül a
 * böngészőt fagyasztaná be — az ajtóban, a beléptető képernyőn.
 */
export const MAX_TICKET_PAGES = 20;

export interface TicketListResult<T> {
  /** A ténylegesen betöltött sorok. */
  data: T[];
  /** A szerver szerinti teljes darabszám — EZ kerül a feliratokra. */
  total: number;
}

interface Options {
  /** Oldalméret; alapértelmezésben {@link TICKET_PAGE_SIZE}. */
  pageSize?: number;
  /**
   * Igazzá válik, ha a kérés eredménye már nem kell (a képernyő időközben
   * másik eseményre váltott). Minden oldal után megkérdezzük.
   */
  isStale?: () => boolean;
  /** Suppresses the api client's global error toast; the caller reports failures. */
  silentError?: boolean;
  timeout?: number;
}

/** Egy válasz sorai — a végpont `{ data, meta }`-t ad, de a régi hívók csupasz tömbbel is számoltak. */
function rowsOf(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  const data = (body as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(data) ? data : [];
}

/** A válasz `meta.total`-ja, ha van — különben `null`, és a hívó dönt. */
function metaTotalOf(body: unknown): number | null {
  const total = (body as { meta?: { total?: unknown } } | null | undefined)?.meta?.total;
  return typeof total === 'number' ? total : null;
}

/**
 * Végiglapozza a `GET /tickets` listát, és a teljes eredményt adja vissza.
 *
 * @param params A hívó saját szűrői (`eventId`, `search`, …). Minden oldalon
 *   változatlanul mennek tovább; a `page` és a `limit` innen származik.
 * @returns A sorok és a szerver szerinti darabszám, vagy `null`, ha az
 *   {@link Options.isStale} őr közben elavultnak jelezte a kérést.
 */
export async function fetchAllTickets<T = unknown>(
  params: Record<string, unknown>,
  options: Options = {}
): Promise<TicketListResult<T> | null> {
  const pageSize = options.pageSize ?? TICKET_PAGE_SIZE;
  const rows: T[] = [];
  let total = 0;

  for (let page = 1; page <= MAX_TICKET_PAGES; page++) {
    const response = await api.get('/tickets', {
      params: { ...params, page, limit: pageSize },
      ...(options.silentError ? { silentError: true } : {}),
      ...(options.timeout ? { timeout: options.timeout } : {}),
    } as never);

    // Az őr a válasz UTÁN fut, még a feldolgozás előtt: egy elavult oldal sem
    // kerülhet be a listába, és nem indulhat utána újabb kérés.
    if (options.isStale?.()) return null;

    const body = response?.data;
    const batch = rowsOf(body) as T[];
    rows.push(...batch);

    const metaTotal = metaTotalOf(body);
    // `meta` nélküli (örökölt, csupasz tömbös) válasznál a tömb hossza az
    // EGYETLEN elérhető forrás — nem kényelmi választás.
    total = metaTotal ?? rows.length;

    // Csupasz tömb: nincs lapozási információ, egyetlen menetben megkaptuk.
    if (Array.isArray(body)) break;
    // A szerver nem ad többet, bármit is állít a `total`.
    if (batch.length === 0) break;
    // Megvan az egész.
    if (rows.length >= total) break;
  }

  return { data: rows, total };
}
